<template>
  <Head title="Conca del Sole | Piscina e Bar a Vidiciatico" />

  <main class="w-full">
    <Header />

    <div class="bg-gray-100">
      <section
        class="cover bg-blue-teal-gradient relative bg-blue-600 px-4 sm:px-8 lg:px-16 xl:px-40 2xl:px-64 overflow-hidden py-48 flex items-center min-h-screen">
        <div class="w-full h-full absolute top-0 left-0 z-0">
          <img alt="" class="w-full h-full object-cover opacity-20" src="images/cover-bg-2.jpg" />
        </div>

        <div class="lg:w-3/4 xl:w-2/4 relative z-10 h-100 lg:mt-16">
          <div>
            <h1 class="text-white text-4xl md:text-5xl xl:text-5xl font-bold leading-tight">Piscina "Conca del
              Sole"</h1>
            <p class="text-blue-100 text-xl md:text-2xl leading-snug mt-4">Non una semplice piscina...</p>
            <p class="text-blue-100 text-xl md:text-2xl leading-snug">con l’acqua sempre riscaldata...</p>
          </div>
        </div>
      </section>
    </div>

    <!-- Pool Status Banner -->
    <section class="relative px-4 py-12 sm:px-8 lg:px-16 xl:px-40 2xl:px-64 bg-gradient-to-r from-red-50 to-orange-50">
      <div class="text-center max-w-4xl mx-auto">
        <div v-if="poolStatus.is_open" class="inline-block bg-green-500 text-white px-6 py-3 rounded-lg mb-4">
          <h3 class="text-2xl font-bold">Piscina Aperta!</h3>
          <p class="text-sm mt-1">{{ poolStatus.season_name }}</p>
          <p class="text-sm" v-if="poolStatus.season_start_date">
            Dal {{ formatDate(poolStatus.season_start_date) }} al {{ formatDate(poolStatus.season_end_date) }}
          </p>
          <p class="text-sm" v-else>Fino al {{ formatDate(poolStatus.season_end_date) }}</p>
        </div>
        <div v-else class="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg mb-4">
          <h3 class="text-2xl font-bold">Piscina Chiusa</h3>
          <p class="text-sm mt-1" v-if="poolStatus.next_opening">
            Riapertura - {{ formatDate(poolStatus.next_opening) }}
          </p>
          <p class="text-sm mt-1" v-else>
            Ci vediamo l'anno prossimo!
          </p>
        </div>
      </div>
    </section>

    <section class="relative px-4 py-16 sm:px-8 lg:px-16 xl:px-40 2xl:px-64 lg:py-32 bg-gray-50">
      <div class="flex flex-col lg:flex-row lg:-mx-8">
        <div class="w-full lg:w-1/2 lg:px-8">
          <h2 class="text-3xl leading-tight font-bold mt-4">Benvenuti a Vidiciatico</h2>
          <p class="text-lg mt-4 font-semibold">La tradizione culinaria incontra l'ospitalità montana!</p>
          <p class="mt-2 leading-relaxed">Conca del Sole è uno stabilimento con piscina, parco, baby park, ristorante e
            bar situato nel cuore di Vidiciatico nel Comune di Lizzano in Belvedere.</p>
        </div>

        <div class="w-full lg:w-1/2 lg:px-8 mt-12 lg:mt-0">
          <div class="md:flex">
            <div>
              <div v-if="poolStatus.is_open" class="bg-green-500 w-16 h-16 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <div v-else class="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9M9.9 4.8L12 6l2.1-1.2M9.9 19.2L12 18l2.1 1.2M19.3 10.2L17.2 9V6.6M4.7 13.8L6.8 15v2.4M17.2 17.4V15l2.1-1.2M6.8 6.6V9l-2.1 1.2"></path>
                </svg>
              </div>
            </div>
            <div class="md:ml-8 mt-4 md:mt-0">
              <h4 class="text-xl font-bold leading-tight" v-if="poolStatus.is_open">
                Piscina Aperta!
              </h4>
              <h4 class="text-xl font-bold leading-tight" v-else>
                Piscina Temporaneamente Chiusa
              </h4>
              <p class="mt-2 leading-relaxed" v-if="poolStatus.is_open">
                La piscina è aperta! Prenota il tuo ingresso e goditi una giornata di relax.
                Stagione: {{ poolStatus.season_name }}
              </p>
              <p class="mt-2 leading-relaxed" v-else>
                La piscina e il parco acquatico
                <span v-if="poolStatus.next_opening">riapriranno il {{ formatDate(poolStatus.next_opening) }}.</span>
                <span v-else>riapriranno l'anno prossimo.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <flash-messages />

    <!-- Booking CTA Section -->
    <section v-if="poolStatus.bookings_open" class="relative flex items-center justify-center px-4 py-10 sm:py-14 sm:px-8 lg:px-16 xl:px-40 2xl:px-64 bg-gradient-to-r from-indigo-600 to-blue-600">
      <div class="max-w-4xl mx-auto text-center w-full">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">Prenota il Tuo Ingresso</h2>
        <p class="text-sm sm:text-lg text-blue-100 mb-6">
          Scegli la data e prenota online per garantirti l'accesso alla piscina!
        </p>
        <a
          href="https://widget.spiagge.it/stabilimenti-balneari/prenotazione/it-bo-40042-piscina-conca-del-sole/"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-block bg-white text-indigo-600 px-6 py-3 sm:px-7 sm:py-3 rounded-lg font-bold text-base sm:text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Prenota Ora →
        </a>
      </div>
    </section>

    <!-- Pool Closed Banner (replaces the booking CTA when bookings are closed) -->
    <section v-else class="relative flex items-center justify-center px-4 py-10 sm:py-14 sm:px-8 lg:px-16 xl:px-40 2xl:px-64 bg-gradient-to-r from-gray-700 to-gray-900">
      <div class="max-w-4xl mx-auto text-center w-full">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">Piscina Chiusa</h2>
        <p class="text-sm sm:text-lg text-gray-200 mb-2" v-if="poolStatus.next_opening">
          Le prenotazioni riapriranno per la stagione che inizia il {{ formatDate(poolStatus.next_opening) }}.
        </p>
        <p class="text-sm sm:text-lg text-gray-200 mb-2" v-else>
          Ci vediamo l'anno prossimo!
        </p>
        <p class="text-sm sm:text-base text-gray-300">
          Per informazioni chiamate il
          <a class="font-semibold underline" href="tel:+393394557158">+39 339 455 7158</a>
        </p>
      </div>
    </section>

    <Footer />
  </main>
</template>

<script>
import FlashMessages from "@/Pages/Dashboard/Shared/FlashMessages.vue";
import Footer from "@/Pages/Home/Shared/Footer.vue";
import Header from "@/Pages/Home/Shared/Header.vue";
import { Head } from "@inertiajs/inertia-vue3";
import moment from "moment";
import "moment/locale/it"; // Import Italian locale for moment.js

export default {
  components: {
    Header,
    Footer,
    Head,
    FlashMessages,
  },
  props: {
    poolStatus: Object,
    restaurantOpen: Boolean,
  },
  data() {
    return {
      date: new Date(),
    };
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      return moment(date).locale("it").format('DD MMMM YYYY');
    },
  },
};
</script>

<style>
#menu-toggle:checked + #menu {
  display: block;
}

#dropdown-toggle:checked + #dropdown {
  display: block;
}

a,
span {
  position: relative;
  text-decoration: none;
  transition: all 0.3s ease;
}

a.arrow,
span.arrow {
  display: flex;
  align-items: center;
  font-weight: 600;
  line-height: 1.5;
}

a.arrow .arrow_icon,
span.arrow .arrow_icon {
  position: relative;
  margin-left: 0.5em;
}

a.arrow .arrow_icon svg,
span.arrow .arrow_icon svg {
  transition: transform 0.3s 0.02s ease;
  margin-right: 1em;
}

a.arrow .arrow_icon::before,
span.arrow .arrow_icon::before {
  content: '';
  display: block;
  position: absolute;
  top: 50%;
  left: 0;
  width: 0;
  height: 2px;
  background: #38b2ac;
  transform: translateY(-50%);
  transition: width 0.3s ease;
}

a.arrow:hover .arrow_icon::before,
span.arrow:hover .arrow_icon::before {
  width: 1em;
}

a.arrow:hover .arrow_icon svg,
span.arrow:hover .arrow_icon svg {
  transform: translateX(0.75em);
}

/* .cover {
  border-bottom-right-radius: 128px;
} */

.bg-blue-teal-gradient {
  background: rgb(49, 130, 206);
  background: linear-gradient(90deg, rgba(49, 130, 206, 1) 0%, rgba(56, 178, 172, 1) 100%);
}

/* @media (min-width: 1024px) {
  .cover {
    border-bottom-right-radius: 256px;
  }
} */
</style>
